"use client";
import React, { useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import ReactMarkdown from "react-markdown";

interface Picture {
  data: {
    id: number;
    attributes: {
      name: string;
      url: string;
      alternativeText: string;
    };
  };
}

interface Content2Props {
  data: {
    id: number;
    title: string;
    description: string;
    image1: Picture;
    image2: Picture;
    image3: Picture;
    image4: Picture;
    points: string;
    isReverse: boolean;
    isLive: boolean;
    useH1Tag?: boolean;
  };
}

export default function Content2({ data }: Content2Props) {
  const imageContainersRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("image-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );

    imageContainersRef.current.forEach((container) => {
      if (container) {
        observer.observe(container);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    data.isLive && (
      <>
        <section
          className="mx-auto px-8 lg:py-12 py-12 bg-th-bg-col-rich overflow-hidden"
          id="content-section"
          style={{
            backgroundImage: `url("https://oextech.blr1.digitaloceanspaces.com/AiCodePro/important/content2-bg-3.svg")`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            position: "relative",
          }}
        >
          <div
            className={`flex lg:flex-row flex-col ${
              data.isReverse ? "lg:flex-row-reverse" : ""
            } justify-center mx-auto lg:gap-8 md:gap-12 gap-0 lg:w-[1200px] w-[360px] md:w-[700px]`}
          >
            <div className="flex flex-col lg:w-1/2 justify-center">
            {
              data?.useH1Tag ? (
              <h1 className="text-4xl font-bold mb-12">{data?.title}</h1>
              ) : (
              <h2 className="text-4xl font-bold mb-12">{data?.title}</h2>
              )
            }
              {data.description && (
                <p className="text-gray-800 text-xl font-semibold mb-4">
                  {data?.description}
                </p>
              )}
              <div className="flex flex-col justify-center items-center">
                <ul className="list-disc pl-2 md:pl-6 lg:pl-8 text-black text-md text-left">
                  {data.points
                    .split(".")
                    .filter((point) => point.trim() !== "")
                    .map((point, index) => (
                      <li key={index} className="mb-2">
                        <ReactMarkdown>{point.trim()}</ReactMarkdown>
                      </li>
                    ))}
                </ul>
              </div>
            </div>
            <div className="relative lg:w-1/2 h-96 mt-8">
              <div
                className={`absolute w-[150px] h-[150px] bg-[#ffffff] bg-opacity-10 rounded-full lg:top-[-40px] top-[-30px] lg:right-[470px] md:top-[-30px] md:right-[540px] right-[240px] ${
                  data.isReverse ? "lg:top-[4px] lg:left-[-5px]" : ""
                }`}
              ></div>
              <div className="absolute w-[150px] h-[150px] bg-white bg-opacity-10 rounded-full bottom-[-50px] right-[-9px] md:right-[-15px] "></div>

              <Swiper
                className="custom-swiper"
                pagination={{
                  dynamicBullets: false,
                }}
                modules={[Autoplay]}
                autoplay={{ delay: 2000, disableOnInteraction: false }}
                loop={true}
                speed={3000}
                spaceBetween={30}
                slidesPerView={1}
                breakpoints={{
                  640: { slidesPerView: 1 },
                  768: { slidesPerView: 1 },
                  1024: { slidesPerView: 1 },
                }}
              >
                {data?.image1?.data?.attributes?.url && (
                  <SwiperSlide>
                    <div
                      className="flex items-center justify-center w-full h-full mt-4"
                      ref={(el) => {
                        imageContainersRef.current[0] = el;
                      }}
                    >
                      <Image
                        src={data?.image1?.data?.attributes?.url}
                        alt={
                          data?.image1?.data?.attributes?.alternativeText ||
                          "image1"
                        }
                        width={290}
                        height={150}
                        className="object-contain w-full h-full rounded-lg"
                      />
                    </div>
                  </SwiperSlide>
                )}

                {data?.image2?.data?.attributes?.url && (
                  <SwiperSlide>
                    <div
                      className="flex items-center justify-center w-full h-full mt-4"
                      ref={(el) => {
                        imageContainersRef.current[1] = el;
                      }}
                    >
                      <Image
                        src={data?.image2?.data?.attributes?.url}
                        alt={
                          data?.image2?.data?.attributes?.alternativeText ||
                          "image2"
                        }
                        width={290}
                        height={150}
                        className="object-contain w-full h-full rounded-lg"
                      />
                    </div>
                  </SwiperSlide>
                )}

                {data?.image3?.data?.attributes?.url && (
                  <SwiperSlide>
                    <div
                      className="flex items-center justify-center w-full h-full mt-4"
                      ref={(el) => {
                        imageContainersRef.current[2] = el;
                      }}
                    >
                      <Image
                        src={data?.image3?.data?.attributes?.url}
                        alt={
                          data?.image3?.data?.attributes?.alternativeText ||
                          "image3"
                        }
                        width={290}
                        height={150}
                        className="object-contain w-full h-full rounded-lg"
                      />
                    </div>
                  </SwiperSlide>
                )}

                {data?.image4?.data?.attributes?.url && (
                  <SwiperSlide>
                    <div
                      className="flex items-center justify-center w-full h-full mt-4"
                      ref={(el) => {
                        imageContainersRef.current[3] = el;
                      }}
                    >
                      <Image
                        src={data?.image4?.data?.attributes?.url}
                        alt={
                          data?.image4?.data?.attributes?.alternativeText ||
                          "image4"
                        }
                        width={290}
                        height={150}
                        className="object-contain w-full h-full rounded-lg"
                      />
                    </div>
                  </SwiperSlide>
                )}
              </Swiper>
            </div>
          </div>
        </section>
      </>
    )
  );
}
